import Highcharts from 'highcharts'

import { decodeText } from '@aytm/app/stats/lib/util/helpers'
import { buildBaseConfig } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/baseConfig'
import { removeTagsFromText } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'

// Highcharts 11 AST sanitizer drops any SVG attribute not in `allowedAttributes`
// for XSS safety. Defs we build via chart.renderer.definition (markers, filters,
// gradients) need attributes like stop-color, flood-color, viewBox, gradientUnits,
// stdDeviation, operator, in/in2/result — none are in the default allowlist, so
// without this extension the produced <marker>/<filter>/<linearGradient> elements
// render empty (visible as missing arrow heads, missing outline glow on series
// line, missing dropShadow on the timeslice capsule, and broken heatmap fill).
const EMD_EXTRA_SVG_ATTRS = [
  // the arrow-head <polyline>: without these it is emitted with no geometry and no
  // rotation, so the marker renders empty (Highcharts logs warning #33 for each)
  'points',
  'transform',
  'stop-color',
  'stop-opacity',
  'flood-color',
  'flood-opacity',
  'viewBox',
  'markerUnits',
  'gradientUnits',
  'stdDeviation',
  'operator',
  'in',
  'in2',
  'result',
  'radius',
  'dx',
  'dy'
]
EMD_EXTRA_SVG_ATTRS.forEach(attr => {
  if (!Highcharts.AST.allowedAttributes.includes(attr)) {
    Highcharts.AST.allowedAttributes.push(attr)
  }
})

// Same sanitizer has a TAG allowlist (`AST.allowedTags`). Filter primitives we
// build inside `outline-*` / `dropShadow` / `gaussianBlur` defs are not in the
// default set — without this extension Highcharts logs warning #33 and silently
// drops `<feMorphology>` / `<feFlood>` / `<feComposite>` children, leaving the
// outline filter empty and the series-line outline glow invisible. We add the
// marker/polyline tags too: `<marker>` itself is allowed but the inner
// `<polyline>` and `<filter>` primitives need explicit opt-in across Highcharts 11.
const EMD_EXTRA_SVG_TAGS = [
  'marker',
  'polyline',
  'filter',
  'feMorphology',
  'feFlood',
  'feComposite',
  'feDropShadow',
  'feGaussianBlur'
]
EMD_EXTRA_SVG_TAGS.forEach(tag => {
  if (!Highcharts.AST.allowedTags.includes(tag)) {
    Highcharts.AST.allowedTags.push(tag)
  }
})

const ARROW_MARKER_ATTRS = {
  viewBox: '0 0 20 10',
  refX: '5',
  refY: '5',
  markerUnits: 'strokeWidth',
  markerWidth: '16',
  markerHeight: '32'
}
const ARROW_POINTS = '0,0 20,5 0,10 5,5'
const ARROW_FILL = '#444444'

// Linear interpolation — replaces d3.scale.linear().domain([d0,d1]).range([r0,r1]).
const linearScale =
  ([d0, d1], [r0, r1]) =>
  v =>
    ((v - d0) / (d1 - d0)) * (r1 - r0) + r0

// `lines.text` is set on batch[0] from chart.coffee:181-182.
// y < 0 → bottom label; y >= 0 → top label.
const lineText = (lines, y) => {
  if (!lines.text) return ''
  return y < 0 ? lines.text[0] : lines.text[1]
}

const formatTime = time => {
  const h = Math.floor(time / 3600)
  const m = Math.floor((time % 3600) / 60)
  const s = Math.floor((time % 3600) % 60)
  const hd = h > 0 ? `${h}:` : ''
  const md = m > 0 ? `${m}:` : '00:'
  const sd = s > 0 ? (s < 10 ? `0${s}` : `${s}`) : '00'
  return hd + md + sd
}

// chartContainer uses `chartMode='emdline'` as the registry key — that name
// must NOT propagate to Highcharts `chart.type`, so we derive the actual
// series type internally from EMD's lineMode + viewMode (legacy parity:
// only viewMode === 'aggregated' gets the area/areaspline fill — the default
// `timeslice` / non-aggregated views render as line/spline).
const getSeriesType = (lineMode, viewMode) => {
  if (viewMode === 'aggregated') {
    if (lineMode === 'precise') return 'area'
    if (lineMode === 'smooth') return 'areaspline'
    if (lineMode === 'segmented') return 'area'
  }
  if (lineMode === 'precise') return 'line'
  if (lineMode === 'smooth') return 'spline'
  if (lineMode === 'segmented') return 'line'
  return 'line'
}

const buildEmdLineIngot = ({ width, height, margin, lineMode, viewMode, timeslice, timeVals, mouseTracking }) => {
  const seriesType = getSeriesType(lineMode, viewMode)
  const xMax = timeVals.length ? timeVals[timeVals.length - 1] : 0

  return buildBaseConfig({
    chart: {
      animation: true,
      margin,
      width,
      height,
      type: seriesType,
      backgroundColor: ''
    },
    plotOptions: {
      line: {
        dataLabels: {
          align: 'center',
          enabled: lineMode === 'segmented',
          formatter() {
            return this.point.text
          },
          position: 'center',
          shadow: false,
          style: { fontSize: '14px', lineHeight: '14px', textOutline: false },
          useHTML: false,
          verticalAlign: 'middle',
          zIndex: 2000
        }
      },
      series: {
        borderWidth: 5,
        shadow: false,
        states: {
          inactive: { opacity: 1 },
          hover: { enabled: false }
        },
        marker: {
          enabled: false,
          states: {
            inactive: { opacity: 1 },
            hover: { enabled: false }
          }
        },
        // off while the cursor is pinned, so the hover label stays with the pinned marker
        enableMouseTracking: false !== mouseTracking
      }
    },
    legend: { enabled: false },
    tooltip: {
      backgroundColor: '',
      borderWidth: 0,
      distance: 10,
      enabled: timeslice || (!timeslice && lineMode !== 'segmented'),
      formatter() {
        return this.point.text
      },
      hideDelay: 50,
      outside: false,
      shared: false,
      style: {
        color: '#1A1A1A',
        fontFamily: 'Ubuntu, Calibri, Segoe, Helvetica, Arial',
        fontSize: '14px',
        padding: 5
      },
      useHTML: false
    },
    xAxis: {
      gridLineWidth: 0,
      labels: { enabled: false },
      lineColor: 'rgba(68, 68, 68, 0.5)',
      lineWidth: 1,
      max: xMax,
      min: 0,
      offset: -(height - margin[0] * 2) / 2,
      plotLines: [],
      tickLength: 3,
      tickColor: 'rgba(68, 68, 68, 0.5)',
      title: { enabled: false, tickInterval: 10 },
      visible: true
    },
    yAxis: {
      gridLineWidth: 0,
      labels: { enabled: false },
      lineWidth: 0,
      max: 100,
      min: -100,
      tickLength: 0,
      title: { enabled: false },
      visible: true
    }
  })
}

// Normalizes y: [50,100] → [0,100] (top) and [0,50] → [-100,0] (bottom).
const calcLineValues = ({ lines, lineMode, timeslice }) => {
  const lineData = lines[lineMode]
  if (!lineData) return []

  const toTop = linearScale([50, 100], [0, 100])
  const toBottom = linearScale([0, 50], [-100, 0])
  const segmented = lineMode === 'segmented'

  const lineValues = lineData.map(el => {
    const yOriginal = el.y
    const calcY = yOriginal < 0 ? toBottom(yOriginal) : toTop(yOriginal)

    // Master's markup: one bold, centred wrapper around all three lines, so the wording and the
    // time come out bold and on the value's axis. `text-anchor: middle` is the SVG property that
    // centres the lines on the tooltip's x (`text-align` is HTML-only and has no effect on a
    // `useHTML: false` tooltip); closing the wrapper after the value, as this used to, left the
    // other two lines unbold and shifted off it.
    let text = `<b style='text-align: center; text-anchor: middle;'><b>${Math.abs(Math.round(parseFloat(calcY) * 10) / 10)}</b>`
    if (!timeslice) {
      text += `<br/><span style='font-size: 12px;'>${lineText(lines, calcY)}</span>`
    }
    if (timeslice || !segmented) {
      text += `<br/><span style='font-size: 12px;'>${formatTime(el.x)}</span>`
    }
    text += '</b>'

    return {
      text,
      dataLabels: { enabled: false, y: yOriginal < 50 ? 25 : -27 },
      y: calcY,
      x: el.x,
      yOriginal
    }
  })

  if (segmented && lines.segments) {
    Object.values(lines.segments).forEach(cur => {
      if (!cur[0] || !cur[1]) return
      const mx = (cur[1].x + cur[0].x) / 2
      const closest = lineValues.reduce((p, c) => (Math.abs(c.x - mx) < Math.abs(p.x - mx) ? c : p))
      const idx = lineValues.findIndex(l => l.x === closest.x)
      if (idx > 0) lineValues[idx].dataLabels.enabled = true
    })
  }

  return lineValues
}

const buildChangePointsPlotLines = (changePoints = []) =>
  changePoints.map(value => ({
    color: {
      linearGradient: [0, 0, 0, '100%'],
      stops: [
        ['5%', 'rgba(68, 68, 68, 0)'],
        ['50%', 'rgba(68, 68, 68, 0.3)'],
        ['95%', 'rgba(68, 68, 68, 0)']
      ]
    },
    width: 4,
    value
  }))

// Hidden-span text measurer for drawAxis's rotated batch labels.
const countLabelW = (text, fontSize = 14) => {
  if (text == null) return 0
  const span = document.createElement('span')
  span.style.cssText =
    `font-family: Ubuntu, Calibri, Segoe, Helvetica, Arial; ` +
    `font-size: ${fontSize}px; line-height: 1; ` +
    `position: absolute; top: 0; opacity: 0; visibility: hidden; right: 0;`
  span.textContent = text
  document.body.appendChild(span)
  const w = span.offsetWidth
  document.body.removeChild(span)
  return w
}

// SVG <defs> consumed by:
//   arrow-up/arrow-down         — drawAxis Y-axis arrowheads
//   dropShadow                  — timeslice capsule
//   gaussianBlur                — Heatmap overlay mask (cross-file consumer)
//   batch-gradient-{id}-{i}     — Heatmap fill (cross-file consumer)
//   outline-{id}-{i}            — series.graph CSS filter
// feDropShadow is SVG2 (Safari < 15.4 unsupported) — no fallback, matches legacy.
const drawDefs = (chart, { id, batches }) => {
  // chart.events.render fires repeatedly (resize, redraw, series toggle). Defs
  // are id-based and document-wide, so re-running drawDefs creates duplicate
  // <marker>/<filter>/<linearGradient> with the same id — invalid SVG and
  // wasted DOM. Build once per (chart instance × questionKey).
  if (chart.customDefsBuilt === id) return
  chart.customDefsBuilt = id

  const renderer = chart.renderer

  for (const [markerId, transform] of [
    ['arrow-up', 'translate(0, 16), rotate(-90)'],
    ['arrow-down', 'translate(10, 0) rotate(90)']
  ]) {
    renderer.definition({
      tagName: 'marker',
      attributes: { id: markerId, ...ARROW_MARKER_ATTRS },
      children: [
        {
          tagName: 'polyline',
          attributes: {
            points: ARROW_POINTS,
            fill: ARROW_FILL,
            transform
          }
        }
      ]
    })
  }

  renderer.definition({
    tagName: 'filter',
    attributes: { id: 'dropShadow' },
    children: [
      {
        tagName: 'feDropShadow',
        attributes: { dx: 0, dy: 3, stdDeviation: 3, 'flood-opacity': '0.16' }
      }
    ]
  })

  renderer.definition({
    tagName: 'filter',
    attributes: { id: 'gaussianBlur' },
    children: [{ tagName: 'feGaussianBlur', attributes: { stdDeviation: 8 } }]
  })

  // Per-batch defs: linearGradient (heatmap fill) + outline filter (series.graph.style.filter).
  // `batches` is passed separately (not derived from `lines`, which is batch[0] only)
  // so per-batch defs are still created for the Heatmap overlay.
  batches.forEach((b, i) => {
    const colors = b.colors || []

    renderer.definition({
      tagName: 'linearGradient',
      attributes: {
        id: `batch-gradient-${id}-${i}`,
        x1: '0%',
        y1: '0%',
        x2: '0%',
        y2: '100%',
        gradientUnits: 'userSpaceOnUse'
      },
      children: [
        {
          tagName: 'stop',
          attributes: {
            offset: '0%',
            'stop-color': colors[1],
            'stop-opacity': 0.75
          }
        },
        {
          tagName: 'stop',
          attributes: {
            offset: '100%',
            'stop-color': colors[0],
            'stop-opacity': 0.75
          }
        }
      ]
    })

    renderer.definition({
      tagName: 'filter',
      attributes: { id: `outline-${id}-${i}`, filterUnits: 'userSpaceOnUse' },
      children: [
        {
          tagName: 'feMorphology',
          attributes: {
            in: 'SourceAlpha',
            result: 'expanded',
            operator: 'dilate',
            radius: '1.2'
          }
        },
        { tagName: 'feFlood', attributes: { 'flood-color': colors[1] } },
        { tagName: 'feComposite', attributes: { in2: 'expanded', operator: 'in' } },
        { tagName: 'feComposite', attributes: { in: 'SourceGraphic' } }
      ]
    })
  })
}

// Renders a custom Y-axis (line + '100/0/100' labels + rotated batch labels)
// straight into the Highcharts SVG root. We track created elements ourselves
// (`customYAxisElements`) because Highcharts.renderer doesn't — a sentinel
// would block re-render when `lines.text` changes.
const drawAxis = (chart, { axisHeight, margin, batches }) => {
  if (chart.customYAxisElements) {
    chart.customYAxisElements.forEach(el => el.destroy?.())
  }
  const created = []

  const yAxisLine = chart.renderer
    .path(['M', margin.x, margin.y + 8, 'L', margin.x, axisHeight - 2 * margin.y + 8])
    .attr({
      stroke: '#444444',
      'marker-start': 'url(#arrow-up)',
      'marker-end': 'url(#arrow-down)'
    })
    .add()
  created.push(yAxisLine)

  const labelStyle = {
    fontFamily: 'Ubuntu, Calibri, Segoe, Helvetica, Arial',
    fontSize: '14px',
    color: '#282828'
  }

  created.push(
    chart.renderer
      .text('100', margin.x + 10, margin.y + 5 + 8)
      .css(labelStyle)
      .add()
  )
  created.push(
    chart.renderer
      .text('100', margin.x + 10, axisHeight - margin.y - 10 + 8)
      .css(labelStyle)
      .add()
  )
  created.push(
    chart.renderer
      .text('0', margin.x - 15, axisHeight / 2 + 8)
      .css(labelStyle)
      .add()
  )

  // Only batches[0] gets rotated labels (legacy parity — multi-batch unsupported).
  // Legacy puts text at (0,0) INSIDE a `<g transform="translate(50, 8)">` so the
  // rotate(270) only acts on (-W-15, -20). Highcharts.renderer.text doesn't wrap
  // in a group, so we prepend translate(margin.x, 8) to the transform and pass
  // (0, 0) — otherwise rotate would project (50, 8) off-screen to (-12, 24).
  const batch = batches[0]
  if (batch?.text) {
    const yText1 = decodeText(removeTagsFromText(batch.text[0] || ''))
    const yText2 = decodeText(removeTagsFromText(batch.text[1] || ''))

    if (yText2) {
      created.push(
        chart.renderer
          .text(yText2, 0, 0)
          .css(labelStyle)
          .attr({
            transform: `translate(${margin.x}, 8) rotate(270) translate(-${countLabelW(yText2) + 15}, -${margin.y})`
          })
          .add()
      )
    }
    if (yText1) {
      created.push(
        chart.renderer
          .text(yText1, 0, 0)
          .css(labelStyle)
          .attr({
            transform: `translate(${margin.x}, 8) rotate(270) translate(-${axisHeight - margin.x / 2 - 5}, -${
              margin.y
            })`
          })
          .add()
      )
    }
  }

  chart.customYAxisElements = created
}

// Overlay helpers below use raw createElementNS (NOT chart.renderer) because:
//   1. Z-order requires `insertBefore(g, markersEl)` — between series-group and
//      markers — for hover/pointer to keep working; renderer.add() only appends.
//   2. Tween animations rely on CSS `transform` + `transition: transform Xs linear`.
// They are exported so the React wrapper can call them imperatively via chart-ref
// (animation tick + change:videoTime sync).

const TIMESLICE_WIDTH = 50
const TIMESLICE_TOP_PADDING = 16
const TICK_LINE_HEIGHT = 5

const SVG_NS = 'http://www.w3.org/2000/svg'

// RGB lerp between two hex colors — replaces d3.scale.linear().range([hex1, hex2]).
const hexToRgb = hex => {
  const h = hex.replace('#', '')
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}
const rgbToHex = ([r, g, b]) =>
  '#' +
  [r, g, b]
    .map(v =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')
const interpolateColor = (c0, c1, t) => {
  const [r0, g0, b0] = hexToRgb(c0)
  const [r1, g1, b1] = hexToRgb(c1)
  return rgbToHex([r0 + (r1 - r0) * t, g0 + (g1 - g0) * t, b0 + (b1 - b0) * t])
}

// pointDensity and the model's timeVals share the same ascending order, so
// indexing by either is equivalent. Comparison is done on values rounded to 4
// decimals because `time` comes from Chart.jsx#calcTimeVals which pre-rounds
// (`Math.round(parseFloat(el) * 10000) / 10000`) and pointDensity keys are raw
// parseFloat — a strict === can mismatch on float-precision noise like
// '0.30000000000000004' vs 0.3 → -1 → empty densityVals → renderTimeslice skips
// the tick-creation block and updateTimeslice short-circuits, so no ticks AND
// no tooltip refresh on initial timeslice render.
const round4 = n => Math.round(n * 10000) / 10000
const getPointIdByTime = (lines, time) => {
  if (!lines?.pointDensity) return -1
  return lines.pointDensity.findIndex(([t]) => round4(parseFloat(t)) === time)
}

const findPointAtTime = (chart, time) => {
  const points = chart?.series?.[0]?.data || []
  return points.find(p => p.x === time)
}

// Builds the timeslice capsule: background rect, center line, N density ticks,
// outer ring + inner dot. Inserted between series-group and markers to preserve
// the z-order Highcharts hover relies on.
export const renderTimeslice = (chart, { time, animationTime = 0, lines, height, margin }) => {
  if (!chart || !lines) return

  const colors = lines.color
  const pointDensity = lines.pointDensity
  const point = findPointAtTime(chart, time)
  const x = point?.plotX || 0
  const y = point?.plotY || 0

  const chartId = chart.container.id
  const parentEl = document.querySelector(`#${chartId} svg .highcharts-series-group`)
  const markersEl = document.querySelector(`#${chartId} svg .highcharts-markers`)
  if (!parentEl || !markersEl) return

  const timesliceHeight = height - margin[0] * 2

  const g = document.createElementNS(SVG_NS, 'g')
  g.setAttribute('class', 'highcharts-timeslice')
  g.setAttribute(
    'style',
    `transition: transform ${animationTime}s linear; transform: translate(${x - 25 + 50}px, 20px)`
  )

  const rect = document.createElementNS(SVG_NS, 'rect')
  rect.setAttribute('x', '0')
  rect.setAttribute('y', '0')
  rect.setAttribute('width', String(TIMESLICE_WIDTH))
  rect.setAttribute('rx', String(TIMESLICE_WIDTH / 2))
  rect.setAttribute('ry', String(TIMESLICE_WIDTH / 2))
  rect.setAttribute('height', String(timesliceHeight))
  rect.setAttribute('fill', '#ffffff')
  rect.setAttribute('fill-opacity', '0.75')
  rect.setAttribute('filter', 'url(#dropShadow)')

  const middleLine = document.createElementNS(SVG_NS, 'line')
  middleLine.setAttribute('x1', String(TIMESLICE_WIDTH / 2))
  middleLine.setAttribute('y1', String(TIMESLICE_TOP_PADDING))
  middleLine.setAttribute('x2', String(TIMESLICE_WIDTH / 2))
  middleLine.setAttribute('y2', String(timesliceHeight - TIMESLICE_TOP_PADDING))
  middleLine.setAttribute('stroke', 'rgba(174, 174, 174, 0.6)')

  const circle = document.createElementNS(SVG_NS, 'circle')
  circle.setAttribute('cx', '25')
  circle.setAttribute('cy', '0')
  circle.setAttribute('r', '7')
  circle.setAttribute('style', `transition: transform ${animationTime}s linear; transform: translate(0, ${y}px)`)
  circle.setAttribute('fill', 'white')
  circle.setAttribute('stroke', colors[0])
  circle.setAttribute('stroke-opacity', '0.5')
  circle.setAttribute('stroke-width', '2.5')

  const circleInner = document.createElementNS(SVG_NS, 'circle')
  circleInner.setAttribute('cx', '25')
  circleInner.setAttribute('cy', '0')
  circleInner.setAttribute('r', '2')
  circleInner.setAttribute('style', `transition: transform ${animationTime}s linear; transform: translate(0, ${y}px)`)
  circleInner.setAttribute('fill', 'black')

  g.appendChild(rect)
  g.appendChild(middleLine)

  // Color interpolation: i=0 → colors[1] (bottom), i=N → colors[0] (top).
  // Legacy reversed the d3 range — we keep the swap explicit instead of reversing args.
  const ticksAmount = pointDensity?.[0]?.[1]?.length || 0
  if (ticksAmount > 0) {
    const tickSize = Math.ceil((timesliceHeight - TIMESLICE_TOP_PADDING * 2) / ticksAmount)
    const pointId = getPointIdByTime(lines, time)
    const densityVals = pointId >= 0 ? pointDensity[pointId][1]?.slice().reverse() || [] : []

    densityVals.forEach((item, i) => {
      const itemW = 30 * item
      const tick = document.createElementNS(SVG_NS, 'rect')
      tick.setAttribute('class', 'highcharts-timeslice-ticks')
      tick.setAttribute('fill', interpolateColor(colors[1], colors[0], i / ticksAmount))
      tick.setAttribute('x', '0')
      tick.setAttribute('y', String(TIMESLICE_TOP_PADDING + i * tickSize))
      tick.setAttribute('rx', '3')
      tick.setAttribute('ry', '3')
      tick.setAttribute('height', String(TICK_LINE_HEIGHT))
      tick.setAttribute(
        'style',
        `width: ${itemW}px; transition: all ${animationTime}s linear; transform: translate(${25 - itemW / 2}px, 0);`
      )
      g.appendChild(tick)
    })
  }

  g.appendChild(circle)
  g.appendChild(circleInner)
  parentEl.insertBefore(g, markersEl)
}

// Non-timeslice pointer: outer colored halo + inner dot.
export const renderCircle = (chart, { time, lines }) => {
  if (!chart || !lines) return

  const colors = lines.color
  const point = findPointAtTime(chart, time)
  const x = point?.plotX || 0
  const y = point?.plotY || 0

  const chartId = chart.container.id
  const parentEl = document.querySelector(`#${chartId} svg .highcharts-series-group`)
  if (!parentEl) return

  const g = document.createElementNS(SVG_NS, 'g')
  g.setAttribute('class', 'highcharts-pointer')
  g.setAttribute('x', String(x))
  g.setAttribute('y', String(y))

  const inner = document.createElementNS(SVG_NS, 'circle')
  inner.setAttribute('cx', '50')
  inner.setAttribute('cy', '20')
  inner.setAttribute('r', '2')
  inner.setAttribute('fill', 'black')
  inner.setAttribute('stroke', colors[0])
  inner.setAttribute('stroke-opacity', '0.65')
  inner.setAttribute('stroke-width', '19')
  g.appendChild(inner)

  const center = document.createElementNS(SVG_NS, 'circle')
  center.setAttribute('cx', '50')
  center.setAttribute('cy', '20')
  center.setAttribute('r', '5')
  center.setAttribute('fill', 'black')
  center.setAttribute('stroke', 'white')
  center.setAttribute('stroke-width', '5')
  g.appendChild(center)

  parentEl.appendChild(g)
}

// Style mutations go through setAttribute('style', ...) because querySelector(All)
// returns the generic `Element` type, which lacks `.style` in TS-strict checking.
// SVGElement/HTMLElement both accept inline style via setAttribute identically.
const setStyle = (el, styleText) => el.setAttribute('style', styleText)

const transitionPart = (property, animationTime) =>
  animationTime != null ? `; transition: ${property} ${animationTime}s linear` : ''

// On duplicate-x points we take the LAST match (legacy parity). A time that matches no point
// exactly (snap passthrough on empty timeVals, series crop) resolves to the nearest one instead
// of silently doing nothing — upstream guards dedupe the repeat signal, so a skipped one-shot
// here would freeze the marker with no later event to unstick it.
const pointAtTime = (chart, time) => {
  const points = chart.series?.[0]?.data || []
  const matched = points.filter(p => p.x === time)
  if (matched.length) return matched[matched.length - 1]

  let nearest = null
  for (const p of points) {
    if (p?.x == null) continue
    if (!nearest || Math.abs(p.x - time) < Math.abs(nearest.x - time)) nearest = p
  }
  return nearest
}

export const updateTimeslice = (chart, { time, animationTime = 0, lines }) => {
  if (!chart || !lines) return
  const chartId = chart.container.id
  const timesliceEl = document.querySelector(`#${chartId} .highcharts-timeslice`)
  if (!timesliceEl) return

  const point = pointAtTime(chart, time)
  if (!point) return

  const pointId = getPointIdByTime(lines, point.x)
  const densityVals = pointId >= 0 ? lines.pointDensity[pointId][1]?.slice().reverse() : null
  if (!densityVals) return

  setStyle(
    timesliceEl,
    `transform: translate(${point.plotX - 25 + 50}px, 20px)${transitionPart('transform', animationTime)}`
  )

  timesliceEl.querySelectorAll('.highcharts-timeslice-ticks').forEach((tick, i) => {
    const w = 30 * (densityVals[i] || 0)
    setStyle(tick, `width: ${w}px; transform: translate(${25 - w / 2}px, 0)${transitionPart('all', animationTime)}`)
  })

  timesliceEl.querySelectorAll('circle').forEach(c => {
    setStyle(c, `transform: translate(0, ${point.plotY}px)${transitionPart('transform', animationTime)}`)
  })

  chart.tooltip?.refresh(point)
}

export const updateCircle = (chart, { time, animationTime = 0, lines }) => {
  if (!chart || !lines) return
  const chartId = chart.container.id
  const pointerEl = document.querySelector(`#${chartId} .highcharts-pointer`)
  if (!pointerEl) return

  const point = pointAtTime(chart, time)
  if (!point) return

  setStyle(
    pointerEl,
    `transform: translate(${point.plotX}px, ${point.plotY}px)${transitionPart('transform', animationTime)}`
  )

  chart.tooltip?.refresh(point)
}

// render* helpers don't trigger tooltip.refresh themselves — `update*` does.
// Calling them in sequence makes the initial tooltip appear on mount.
const buildOnLoad = ({ lines, timeslice, videoTime, height, margin, onMount, onHeatmapMount, onWindmapMount }) =>
  function onLoad() {
    if (!lines) return
    const chart = this
    const chartId = chart.container.id
    const initialTime = videoTime || 0

    setTimeout(() => {
      if (timeslice) {
        renderTimeslice(chart, { time: initialTime, animationTime: 0, lines, height, margin })
        updateTimeslice(chart, { time: initialTime, animationTime: 0, lines })
      } else {
        renderCircle(chart, { time: initialTime, lines })
        updateCircle(chart, { time: initialTime, animationTime: 0, lines })
        onHeatmapMount?.(chartId)
        onWindmapMount?.(chartId)
      }
      onMount?.(chart)
    }, 0)
  }

const closestSample = (xData, val) =>
  xData?.length && Number.isFinite(val) ? xData.reduce((p, c) => (Math.abs(c - val) < Math.abs(p - val) ? c : p)) : null

// Closest-time detection only. The pin / unpin state and the ignore-while-playing rule
// (legacy `slidemode`) live in the emotional-dial chart wrapper's click handler.
const buildOnClick = ({ lines, onTimeUpdate }) =>
  function onClick(event) {
    if (!lines || !event.xAxis) return
    const closest = closestSample(this.series[0].xData, event.xAxis[0].value)
    if (null !== closest) onTimeUpdate?.(closest)
  }

// A click on the line under a hovered point is routed by Highcharts to the series and never reaches
// the chart's click event, so while the cursor follows the mouse the pinning click lands here. The
// hovered point can trail the pointer by an event, so the time is read off the click's position, as
// the chart's click does, not off the point.
const buildOnSeriesClick = ({ lines, onTimeUpdate }) =>
  function onSeriesClick(event) {
    if (!lines || !this.xAxis || !Number.isFinite(event.chartX)) return
    const closest = closestSample(this.xData, this.xAxis.toValue(event.chartX))
    if (null !== closest) onTimeUpdate?.(closest)
  }

export const buildEmdLineOptions = (props = {}) => {
  const {
    questionKey,
    questionId,
    lines,
    batches = [],
    timeslice,
    viewMode,
    lineMode,
    width,
    height,
    margin,
    timeVals = [],
    videoTime,
    mouseTracking,
    onMount,
    onMouseOver,
    onTimeUpdate,
    onHeatmapMount,
    onWindmapMount
  } = props

  const data = buildEmdLineIngot({
    width,
    height,
    margin,
    lineMode,
    viewMode,
    timeslice,
    timeVals,
    mouseTracking
  })

  if (lines) {
    const lineValues = calcLineValues({ lines, lineMode, timeslice })
    data.xAxis.plotLines = lineMode === 'segmented' ? buildChangePointsPlotLines(lines.changePoints) : []

    // ALWAYS provide a positioner — `chart.update()` MERGES tooltip options
    // instead of resetting, so a `{x:10, y:plotY}` positioner set during a
    // prior timeslice render would persist after switching to aggregated /
    // default mode and pin the tooltip to x=10 (visible as "tooltip moves only
    // vertically" — y still follows cursor via plotY, x stuck at 10). In
    // non-timeslice mode we delegate to Highcharts' default `getPosition`
    // which gives the cursor-following behaviour the legacy chart had.
    data.tooltip.positioner = function tooltipPositioner(lw, lh, point) {
      if (timeslice) return { x: 10, y: point.plotY }
      return this.getPosition(lw, lh, point)
    }

    data.series = [
      {
        border: lines.border,
        color: lines.color[0],
        data: lineValues,
        lineWidth: 2
      }
    ]
  } else {
    data.series = []
  }

  // Single events.render handler — overwriting Highcharts events with a second
  // handler silently drops the first one.
  data.chart.events = {
    ...(data.chart.events || {}),
    load: buildOnLoad({
      lines,
      timeslice,
      videoTime,
      height,
      margin,
      onMount,
      onHeatmapMount,
      onWindmapMount
    }),
    click: buildOnClick({ lines, onTimeUpdate }),
    render() {
      const chart = this
      // defs id must match the wrapper's url(#…-{questionId}-{i}) refs.
      // Fall back to questionKey for any caller that doesn't pass questionId.
      drawDefs(chart, { id: questionId ?? questionKey, batches })
      // batches[0].text wins for drawAxis labels (legacy parity); fall back to
      // `batches` when `lines.text` isn't set yet (initial render with empty lines).
      const axisBatches = lines?.text ? [{ text: lines.text }] : batches
      drawAxis(chart, {
        axisHeight: height,
        margin: { x: margin[1], y: margin[0] },
        batches: axisBatches
      })

      if (!lines) return

      // Keep tooltip visible in timeslice / non-segmented modes (legacy parity).
      if (timeslice || lineMode !== 'segmented') {
        chart.tooltip.hide = () => false
      }

      // Highcharts strips unknown `border` from series config — fall back to the
      // closure-captured URL so the outline still renders.
      const fallbackBorder = lines.border
      chart.series.forEach(s => {
        const filterUrl = s.options.border || fallbackBorder
        if (s.graph?.element && filterUrl) {
          s.graph.element.style.filter = filterUrl
        }
      })
    }
  }

  // The wrapper's hover handler ignores the mouse while playing or pinned, and says so by returning
  // false; handing that back to Highcharts cancels the mouse-over's default tooltip refresh, so the
  // label stays with the marker instead of following the pointer.
  data.plotOptions.series.events = { click: buildOnSeriesClick({ lines, onTimeUpdate }) }
  data.plotOptions.series.point = {
    events: {
      mouseOver(event) {
        if (lines && onMouseOver) return onMouseOver(event.target.x)
      }
    }
  }

  return { options: data }
}

export { lineText, formatTime, linearScale, getPointIdByTime, interpolateColor }
